import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Edit, Trash2, Tag, Save, X, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessEntities() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ category: "", entity_type: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | object
  const [toast, setToast] = useState(null);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.set("category", filters.category);
      if (filters.entity_type) params.set("entity_type", filters.entity_type);
      if (filters.q) params.set("q", filters.q);
      const [listR, catR] = await Promise.all([
        fetch(`${API}/business-entities?${params.toString()}`, { headers }).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/business-entities/categories/list`, { headers }).then(r => r.ok ? r.json() : { categories: [] }),
      ]);
      setItems(listR.items || []);
      setCategories(catR.categories || []);
    } catch (e) { showToast("error", "Failed to load"); }
    setLoading(false);
  }, [headers, filters]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      const isNew = editing === "new";
      const url = isNew ? `${API}/business-entities` : `${API}/business-entities/${editing.id}`;
      const method = isNew ? "POST" : "PUT";
      const body = JSON.stringify(form);
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        showToast("error", d.detail || "Save failed");
        return;
      }
      showToast("success", isNew ? "Entity created" : "Entity updated");
      setEditing(null);
      load();
    } catch (e) { showToast("error", "Save failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Deactivate "${item.entity_name}"?`)) return;
    const r = await fetch(`${API}/business-entities/${item.id}`, { method: "DELETE", headers });
    if (r.ok) { showToast("success", "Deactivated"); load(); }
    else showToast("error", "Delete failed");
  };

  const currentCategoryCfg = categories.find(c => c.slug === filters.category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50/30 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-end gap-3" data-testid="business-entities-header">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 inline-flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-violet-600" /> Business Entities
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Add the things you sell — services, doctors, courses, products, menu items.
              The AI auto-tags conversations and creates appointments / orders against these.
            </p>
          </div>
          <button
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold shadow-sm hover:shadow-md"
            data-testid="be-new-btn"
          >
            <Plus className="w-4 h-4" /> New entity
          </button>
        </header>

        {/* Filters */}
        <div className="grid sm:grid-cols-4 gap-3" data-testid="be-filters">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value, entity_type: "" })}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
            data-testid="be-filter-category"
          >
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c.slug} value={c.slug}>{c.name}</option>
            ))}
          </select>
          <select
            value={filters.entity_type}
            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
            data-testid="be-filter-type"
            disabled={!currentCategoryCfg}
          >
            <option value="">All types</option>
            {(currentCategoryCfg?.entity_types || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <div className="sm:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search by name, alias, or pricing"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
              data-testid="be-filter-q"
            />
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="be-list">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Tag className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No entities yet. Click <b>New entity</b> to add one.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Aliases</th>
                  <th className="px-3 py-2 text-left">Pricing</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-violet-50/40" data-testid={`be-row-${it.id}`}>
                    <td className="px-3 py-2.5 font-semibold text-gray-900">{it.entity_name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{it.business_category}</td>
                    <td className="px-3 py-2.5 text-gray-600">{it.entity_type}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">
                      {(it.aliases || []).slice(0, 3).join(", ")}
                      {(it.aliases?.length || 0) > 3 ? ` +${it.aliases.length - 3}` : ""}
                    </td>
                    <td className="px-3 py-2.5 text-gray-700 text-xs">{it.pricing_summary || "—"}</td>
                    <td className="px-3 py-2.5">
                      {it.is_active ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right space-x-1">
                      <button
                        onClick={() => setEditing(it)}
                        className="p-1.5 rounded-md hover:bg-violet-100 text-violet-600"
                        data-testid={`be-edit-${it.id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(it)}
                        className="p-1.5 rounded-md hover:bg-red-100 text-red-500"
                        data-testid={`be-del-${it.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <EntityFormModal
          entity={editing === "new" ? null : editing}
          categories={categories}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl shadow-lg text-sm font-medium text-white ${toast.kind === "success" ? "bg-emerald-600" : "bg-red-600"}`}
          data-testid="be-toast"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function EntityFormModal({ entity, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    business_category: entity?.business_category || categories[0]?.slug || "general",
    entity_type: entity?.entity_type || "",
    entity_name: entity?.entity_name || "",
    aliases_text: (entity?.aliases || []).join(", "),
    location: entity?.location || "",
    pricing_summary: entity?.pricing_summary || "",
    media_links_text: (entity?.media_links || []).join("\n"),
    is_active: entity?.is_active ?? true,
  });
  const cat = categories.find(c => c.slug === form.business_category);
  const submit = (e) => {
    e.preventDefault();
    if (!form.entity_name.trim()) return;
    onSave({
      business_category: form.business_category,
      entity_type: form.entity_type || cat?.entity_types?.[0] || "service",
      entity_name: form.entity_name.trim(),
      aliases: form.aliases_text.split(",").map(s => s.trim()).filter(Boolean),
      location: form.location.trim(),
      pricing_summary: form.pricing_summary.trim(),
      media_links: form.media_links_text.split("\n").map(s => s.trim()).filter(Boolean),
      is_active: form.is_active,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="be-form-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto"
      >
        <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{entity ? "Edit entity" : "New entity"}</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Category">
              <select value={form.business_category}
                onChange={(e) => setForm({ ...form, business_category: e.target.value, entity_type: "" })}
                className="input"
                data-testid="be-form-category"
              >
                {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Entity Type">
              <select value={form.entity_type}
                onChange={(e) => setForm({ ...form, entity_type: e.target.value })}
                className="input"
                data-testid="be-form-type"
              >
                {(cat?.entity_types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Entity Name *">
            <input value={form.entity_name}
              onChange={(e) => setForm({ ...form, entity_name: e.target.value })}
              required className="input"
              data-testid="be-form-name"
              placeholder="e.g. Mens Haircut · Spoken English · Dr. Smitha · iPhone 15 Pro Case"
            />
          </Field>
          <Field label="Aliases (comma-separated)" hint="Helps the AI catch typos and informal names — e.g. 'hair cut, haircut, mens cut'.">
            <input value={form.aliases_text}
              onChange={(e) => setForm({ ...form, aliases_text: e.target.value })}
              className="input"
              data-testid="be-form-aliases"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Pricing Summary" hint="What the AI will quote — e.g. '₹450 · 30 min'">
              <input value={form.pricing_summary}
                onChange={(e) => setForm({ ...form, pricing_summary: e.target.value })}
                className="input"
                data-testid="be-form-pricing"
              />
            </Field>
            <Field label="Location">
              <input value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="input"
                data-testid="be-form-location"
              />
            </Field>
          </div>
          <Field label="Media URLs (one per line)" hint="Brochure, layout, video, image — AI will paste these when asked.">
            <textarea value={form.media_links_text}
              onChange={(e) => setForm({ ...form, media_links_text: e.target.value })}
              rows={3} className="input"
              data-testid="be-form-media"
              placeholder="https://example.com/brochure.pdf"
            />
          </Field>
          <div className="flex items-center gap-2">
            <input id="be-active" type="checkbox" checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="be-form-active"
            />
            <label htmlFor="be-active" className="text-sm text-gray-700">Active (AI will use this entity)</label>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50 sticky bottom-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100">Cancel</button>
          <button type="submit" data-testid="be-form-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
            <Save className="w-3.5 h-3.5" /> Save
          </button>
        </div>
        <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border-radius: 0.5rem; border: 1px solid #e5e7eb; font-size: 0.875rem; background: white; outline: none; } .input:focus { border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139,92,246,0.15); }`}</style>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}
